import { useEffect, useState, useMemo } from 'react';
import { useQuery } from 'react-fetching-library';
import { ALL_ROUTES } from 'config/routes';
import RemoveDialog from 'components/Dialogs/RemoveDialog';
import useFetchList from 'hooks/useFetchList';
import useMetadataUpdate from 'hooks/useMetadataUpdate';
import showServerError from 'helpers/showError';
import { useDispatch, useSelector } from 'react-redux';
import { NULL_QUERY_DELAY } from '../../Shared/constants';
import {
  enqueueSnackbar,
  SNACKBAR_VARIANTS,
  setAccounts,
  getTransitionWithVariable,
  TABLE_KEYS,
  updateCanShowShimmer,
  navigateToRoute,
} from '../../../redux_store';
import { ACCOUNT_COLUMNS } from '../constants';
import DownloadPracticeOptimizationReportModal from './widgets/DownloadPracticeOptimizationReportModal';
import QhsTable from '../../Shared/Table/Table';
import { convertDataFromApi, useGenerateColumns } from '../../Shared/Table/helpers';
import TableActions from './widgets/TableActions';
import AddButton from '../../Shared/Table/widgets/AddButton';
import TableTitle from '../../Shared/Table/widgets/TableTitle';
import TablePageContainer from '../../Shared/Table/widgets/TablePageContainer';

const AccountsList = () => {
  const {
    en: { titles, notifications, dialogs, errors },
  } = useSelector(state => state.i18n);
  const dispatch = useDispatch();
  const { accounts } = useSelector(state => state.accounts);
  const [isOpenRemoveDialog, setOpenRemoveDialog] = useState(false);
  const [removingAccount, setRemovingAccount] = useState({});
  const [isSuccess, setIsSuccess] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [downloadAccount, setDownloadAccount] = useState(null);
  const {
    user,
    isDistributionSponsor,
    metadata: { currentAccount },
  } = useSelector(state => state.user);
  const tableKey = TABLE_KEYS.accountsTable;
  const columns = useGenerateColumns(ACCOUNT_COLUMNS, tableKey, {}, {}).columns;
  const fields = columns.map(columnData => columnData.accessorKey).sort().join();

  const {
    items,
    totalCount,
    reloadData,
    clearFilter,
    onLoadMoreRows,
    loading: dataLoading,
    sort,
    onSort,
    filter,
    onFilter,
    fetchError,
  } = useFetchList({
    baseEndpoint: '/accounts',
    baseParams: { fields: fields },
    tableKey,
    canLoad: fields.length,
  });

  const { query: queryRemove, loading: loadingRemove } = useMetadataUpdate(useQuery, [
    {
      method: 'DELETE',
      endpoint: `/accounts/${removingAccount.uuid}`,
    },
    false,
  ]);

  const [prevTotalCount, setPrevTotalCount] = useState(totalCount);

  useEffect(() => {
    if (prevTotalCount !== totalCount) {
      setPrevTotalCount(totalCount);
    }
  }, [filter, totalCount]);

  useEffect(() => {
    if (items !== accounts) {
      const newModifiedItems = convertDataFromApi(items, tableKey, filter);
      dispatch(setAccounts(newModifiedItems));
    }
  }, [items]);

  const handleRowClick = ({ uuid, businessName }) => {
    const isItChildAccount = uuid !== currentAccount.uuid && isDistributionSponsor;
    dispatch(
      navigateToRoute({
        route: {
          path: (isItChildAccount ? ALL_ROUTES.patient.listDS : ALL_ROUTES.patient.list).path,
          params: {
            currentAccountUuid: currentAccount.uuid,
            accountUuid: isItChildAccount ? uuid : null,
          },
          title: `${titles.patients}: ${businessName}`,
        },
      })
    );
  };

  const handleCloseRemoveDialog = () => {
    setOpenRemoveDialog(false);
    setRemovingAccount({});
  };

  const handleRemove = () => {
    const { uuid, businessName } = removingAccount;
    if (uuid) {
      queryRemove().then(({ error, payload, status }) => {
        if (!error) {
          setIsSuccess(true);
          setTimeout(() => {
            setIsSuccess(false);
          }, 2000);
          dispatch(updateCanShowShimmer(true));
          reloadData();
          dispatch(
            enqueueSnackbar({
              message: getTransitionWithVariable(notifications.accountRemoved, {
                name: businessName,
              }),
              options: {
                variant: SNACKBAR_VARIANTS.success,
              },
            })
          );
        } else {
          setIsFailed(true);
          setTimeout(() => {
            setIsFailed(false);
          }, 2000);
          const options = payload && {
            correlationUUID: payload.correlationUUID,
            userUUID: user.uuid,
          };
          switch (status) {
            case 404: {
              showServerError(dispatch, errors.accountCouldNotFind);
              break;
            }
            case 500: {
              if (payload.errorCode === 1) {
                showServerError(
                  dispatch,
                  getTransitionWithVariable(errors.accountCantBeRemoved, {
                    name: businessName,
                  })
                );
              } else {
                showServerError(dispatch, '', options);
              }
              break;
            }
            default: {
              showServerError(dispatch, '', options);
              break;
            }
          }
        }
        setOpenRemoveDialog(false);
        setRemovingAccount({});
      });
    }
  };

  const reloadAccountsList = hasFilter => {
    if (!hasFilter) {
      clearFilter();
    } else {
      reloadData({ delay: NULL_QUERY_DELAY });
    }
  };

  const handleAccountCreate = () => {
    dispatch(
      navigateToRoute({
        route: {
          path: ALL_ROUTES.account.createDS.path,
          params: { currentAccountUuid: currentAccount.uuid },
          title: titles.createAccount,
        },
      })
    );
  };

  const hasFilters = useMemo(
    () =>
      !!filter?.reduce((accumulator, { value }) => {
        return accumulator + value.toString().replaceAll(' ', '').length;
      }, 0),
    [filter]
  );

  return (
    <TablePageContainer>
      <QhsTable
        columns={columns}
        data={accounts || []}
        fetchNextPage={onLoadMoreRows}
        isFetching={dataLoading}
        isLoading={dataLoading && !accounts}
        totalRowCount={totalCount}
        sorting={sort}
        setSorting={onSort}
        filter={filter}
        setFilters={onFilter}
        tableKey={tableKey}
        onRowClick={handleRowClick}
        showRefreshTableButton
        fetchError={fetchError}
        reloadData={reloadAccountsList}
        menuActions={<AddButton label={titles.account} onClick={handleAccountCreate} />}
        tableTitle={
          <TableTitle titleText={titles.accounts} count={totalCount} countFiltered={hasFilters} />
        }
        renderRowActionMenuItems={({ row, setAnchorEl }) => (
          <TableActions
            row={row}
            setAnchorEl={setAnchorEl}
            setDownloadAccount={setDownloadAccount}
            setOpenRemoveDialog={setOpenRemoveDialog}
            setRemovingAccount={setRemovingAccount}
          />
        )}
      />
      <RemoveDialog
        open={isOpenRemoveDialog}
        onClose={handleCloseRemoveDialog}
        onSubmit={handleRemove}
        title={getTransitionWithVariable(dialogs.confirmRemove, {
          name: titles.account,
        })}
        isLoading={loadingRemove}
        isSuccess={isSuccess}
        isFailed={isFailed}
      />
      <DownloadPracticeOptimizationReportModal
        accountUUID={downloadAccount?.uuid}
        openDownloadPdfModal={!!downloadAccount?.uuid}
        accountName={downloadAccount?.businessName}
        closeDownloadPdfModal={() => {
          setDownloadAccount(null);
        }}
      />
    </TablePageContainer>
  );
};

export default AccountsList;
